import { genComponentStyleHook, mergeToken } from '../../theme';
import genGroupStyle from './group';
// ============================== Shared ==============================
const genSharedButtonStyle = (token) => {
    const { componentCls, iconCls } = token;
    return {
        [componentCls]: {
            outline: 'none',
            position: 'relative',
            display: 'inline-block',
            fontWeight: 400,
            whiteSpace: 'nowrap',
            textAlign: 'center',
            backgroundImage: 'none',
            backgroundColor: 'transparent',
            border: `${token.controlLineWidth}px ${token.controlLineType} transparent`,
            cursor: 'pointer',
            transition: `all ${token.motionDurationSlow} ${token.motionEaseInOut}`,
            userSelect: 'none',
            touchAction: 'manipulation',
            lineHeight: token.lineHeight,
            color: token.colorText,
            '> span': {
                display: 'inline-block',
            },
            // Leave a space between icon and text.
            [`> ${iconCls} + span, > span + ${iconCls}`]: {
                marginInlineStart: token.marginXS,
            },
            [`&${componentCls}-block`]: {
                width: '100%',
            },
        },
    };
};
const genHoverActiveButtonStyle = (hoverStyle, activeStyle) => ({
    '&:not(:disabled)': {
        '&:hover, &:focus': hoverStyle,
        '&:active': activeStyle,
    },
});
// ============================== Shape ===============================
const genCircleButtonStyle = token => ({
    minWidth: token.controlHeight,
    paddingInlineStart: 0,
    paddingInlineEnd: 0,
    borderRadius: '50%',
});
const genRoundButtonStyle = token => ({
    borderRadius: token.controlHeight,
    paddingInlineStart: token.controlHeight / 2,
    paddingInlineEnd: token.controlHeight / 2,
    width: 'auto',
});
// =============================== Type ===============================
const genGhostButtonStyle = (btnCls, textColor, borderColor, textColorDisabled, borderColorDisabled) => ({
    [`&${btnCls}-background-ghost`]: {
        color: textColor || undefined,
        backgroundColor: 'transparent',
        borderColor: borderColor || undefined,
        boxShadow: 'none',
        '&:hover': {
            backgroundColor: 'transparent',
        },
        '&:disabled': {
            cursor: 'not-allowed',
            color: textColorDisabled || undefined,
            borderColor: borderColorDisabled || undefined,
        },
    },
});
const genSolidDisabledButtonStyle = token => ({
    '&:disabled': {
        cursor: 'not-allowed',
        borderColor: token.colorBorder,
        color: token.colorTextDisabled,
        backgroundColor: token.colorBgContainerDisabled,
        boxShadow: 'none',
    },
});
const genSolidButtonStyle = token => (Object.assign({}, genSolidDisabledButtonStyle(token)));
const genPureDisabledButtonStyle = token => ({
    '&:disabled': {
        cursor: 'not-allowed',
        color: token.colorTextDisabled,
    },
});
// Type: Default
const genDefaultButtonStyle = token => (Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, genSolidButtonStyle(token)), { backgroundColor: token.colorBgContainer, borderColor: token.colorBorder, boxShadow: `0 ${token.controlOutlineWidth}px 0 ${token.controlTmpOutline}` }), genHoverActiveButtonStyle({
    color: token.colorPrimaryHover,
    borderColor: token.colorPrimaryHover,
}, {
    color: token.colorPrimaryActive,
    borderColor: token.colorPrimaryActive,
})), genGhostButtonStyle(token.componentCls, token.colorBgContainer, token.colorBgContainer, token.colorTextDisabled, token.colorBorder)), { [`&${token.componentCls}-dangerous`]: Object.assign(Object.assign(Object.assign({ color: token.colorError, borderColor: token.colorError }, genHoverActiveButtonStyle({
        color: token.colorErrorHover,
        borderColor: token.colorErrorHover,
    }, {
        color: token.colorErrorActive,
        borderColor: token.colorErrorActive,
    })), genGhostButtonStyle(token.componentCls, token.colorError, token.colorError, token.colorTextDisabled, token.colorBorder)), genSolidDisabledButtonStyle(token)) }));
// Type: Primary
const genPrimaryButtonStyle = token => (Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, genSolidButtonStyle(token)), { color: token.colorTextLightSolid, backgroundColor: token.colorPrimary, boxShadow: `0 ${token.controlOutlineWidth}px 0 ${token.controlOutline}` }), genHoverActiveButtonStyle({
    backgroundColor: token.colorPrimaryHover,
}, {
    backgroundColor: token.colorPrimaryActive,
})), genGhostButtonStyle(token.componentCls, token.colorPrimary, token.colorPrimary, token.colorTextDisabled, token.colorBorder)), { [`&${token.componentCls}-dangerous`]: Object.assign(Object.assign(Object.assign({ backgroundColor: token.colorError, boxShadow: `0 ${token.controlOutlineWidth}px 0 ${token.colorErrorOutline}` }, genHoverActiveButtonStyle({
        backgroundColor: token.colorErrorHover,
    }, {
        backgroundColor: token.colorErrorActive,
    })), genGhostButtonStyle(token.componentCls, token.colorError, token.colorError, token.colorTextDisabled, token.colorBorder)), genSolidDisabledButtonStyle(token)) }));
// Type: Dashed
const genDashedButtonStyle = token => (Object.assign(Object.assign({}, genDefaultButtonStyle(token)), { borderStyle: 'dashed' }));
// Type: Link
const genLinkButtonStyle = token => (Object.assign(Object.assign(Object.assign({ color: token.colorLink }, genHoverActiveButtonStyle({
    color: token.colorLinkHover,
}, {
    color: token.colorLinkActive,
})), genPureDisabledButtonStyle(token)), { [`&${token.componentCls}-dangerous`]: Object.assign(Object.assign({ color: token.colorError }, genHoverActiveButtonStyle({
        color: token.colorErrorHover,
    }, {
        color: token.colorErrorActive,
    })), genPureDisabledButtonStyle(token)) }));
// Type: Text
const genTextButtonStyle = token => (Object.assign(Object.assign(Object.assign({}, genHoverActiveButtonStyle({
    backgroundColor: token.colorBgTextHover,
}, {
    backgroundColor: token.colorBgTextActive,
})), genPureDisabledButtonStyle(token)), { [`&${token.componentCls}-dangerous`]: Object.assign({ color: token.colorError }, genPureDisabledButtonStyle(token)) }));
const genTypeButtonStyle = token => {
    const { componentCls } = token;
    return {
        [`${componentCls}-default`]: genDefaultButtonStyle(token),
        [`${componentCls}-primary`]: genPrimaryButtonStyle(token),
        [`${componentCls}-dashed`]: genDashedButtonStyle(token),
        [`${componentCls}-link`]: genLinkButtonStyle(token),
        [`${componentCls}-text`]: genTextButtonStyle(token),
    };
};
// =============================== Size ===============================
const genSizeButtonStyle = (token, sizePrefixCls = '') => {
    const { componentCls, iconCls } = token;
    const paddingVertical = Math.max(0, (token.controlHeight - token.fontSize * token.lineHeight) / 2 - token.controlLineWidth);
    const paddingHorizontal = token.padding - token.controlLineWidth;
    const iconOnlyCls = `${componentCls}-icon-only`;
    return [
        // Size
        {
            [`${componentCls}${sizePrefixCls}`]: {
                fontSize: token.fontSize,
                height: token.controlHeight,
                padding: `${paddingVertical}px ${paddingHorizontal}px`,
                borderRadius: token.controlRadius,
                [`&${iconOnlyCls}`]: {
                    width: token.controlHeight,
                    paddingInlineStart: 0,
                    paddingInlineEnd: 0,
                    '> span': {
                        transform: 'scale(1.143)', // 14px -> 16px
                    },
                },
                // Loading
                [`&${componentCls}-loading`]: {
                    opacity: token.opacityLoading,
                    cursor: 'default',
                },
                [`${componentCls}-loading-icon`]: {
                    transition: `width ${token.motionDurationSlow} ${token.motionEaseInOut}, opacity ${token.motionDurationSlow} ${token.motionEaseInOut}`,
                },
                [`&:not(${iconOnlyCls}) ${componentCls}-loading-icon > ${iconCls}`]: {
                    marginInlineEnd: token.marginXS,
                },
            },
        },
        // Shape - patch prefixCls again to override solid border radius style
        {
            [`${componentCls}${componentCls}-circle${sizePrefixCls}`]: genCircleButtonStyle(token),
        },
        {
            [`${componentCls}${componentCls}-round${sizePrefixCls}`]: genRoundButtonStyle(token),
        },
    ];
};
const genSizeBaseButtonStyle = token => genSizeButtonStyle(token);
const genSizeSmallButtonStyle = token => {
    const smallToken = mergeToken(token, {
        controlHeight: token.controlHeightSM,
        padding: token.paddingXS,
        controlRadius: token.controlRadiusSM,
    });
    return genSizeButtonStyle(smallToken, `${token.componentCls}-sm`);
};
const genSizeLargeButtonStyle = token => {
    const largeToken = mergeToken(token, {
        controlHeight: token.controlHeightLG,
        fontSize: token.fontSizeLG,
        controlRadius: token.controlRadiusLG,
    });
    return genSizeButtonStyle(largeToken, `${token.componentCls}-lg`);
};
// ============================== Export ==============================
export default genComponentStyleHook('Button', token => {
    const { controlTmpOutline, colorFillQuaternary, colorFillTertiary } = token;
    const buttonToken = mergeToken(token, {
        colorBgTextHover: colorFillQuaternary,
        colorBgTextActive: colorFillTertiary,
        colorOutlineDefault: controlTmpOutline,
    });
    return [
        // Shared
        genSharedButtonStyle(buttonToken),
        // Size
        genSizeSmallButtonStyle(buttonToken),
        genSizeBaseButtonStyle(buttonToken),
        genSizeLargeButtonStyle(buttonToken),
        // Group (type, ghost, danger, disabled, loading)
        genTypeButtonStyle(buttonToken),
        // Button Group
        genGroupStyle(buttonToken),
    ];
});
